
import React from 'react';
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Settings, LogOut, X } from "lucide-react";
import { Capacitor } from '@capacitor/core';
import { truncateEmail } from '@/utils/stringUtils';
import { ReferralPopover } from './ReferralPopover';
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { SubscriptionPlans } from '@/components/subscription/SubscriptionPlans';

interface HeaderProps {
  userEmail: string;
  profile: any;
  onSettingsClick: () => void;
  onLogout: () => void;
}

const Header: React.FC<HeaderProps> = ({ userEmail, profile, onSettingsClick, onLogout }) => {
  const isMobile = Capacitor.isNativePlatform() || window.innerWidth < 768;
  const displayEmail = truncateEmail(userEmail);
  const [isSubscriptionOpen, setIsSubscriptionOpen] = React.useState(false);
  const [isSettingsOpen, setIsSettingsOpen] = React.useState(false);

  return (
    <div className="flex justify-between items-start mb-8">
      <div className="flex items-start gap-4">
        <Avatar className="h-16 w-16 cursor-pointer" onClick={onSettingsClick}>
          <AvatarImage src={profile?.avatar_url || "/placeholder.svg"} />
          <AvatarFallback>{userEmail?.charAt(0)?.toUpperCase() || 'U'}</AvatarFallback>
        </Avatar>
        <div className="flex flex-col">
          <h1 className="text-2xl font-bold">My Notes</h1>
          <p className="text-gray-600">{displayEmail}</p>
          {profile?.subscription_status === 'trial' && isMobile && (
            <ReferralPopover userId={profile?.id} isMobile={true} />
          )}
        </div>
      </div>
      <div className="flex gap-2">
        {!isMobile && <ReferralPopover userId={profile?.id} />}
        
        <Popover open={isSettingsOpen} onOpenChange={setIsSettingsOpen}>
          <PopoverTrigger asChild>
            <Button variant="ghost" size="icon" aria-label="Open settings">
              <Settings className="h-5 w-5" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-56" align="end" sideOffset={8}>
            <div className="flex flex-col gap-2">
              <Popover open={isSubscriptionOpen} onOpenChange={setIsSubscriptionOpen}>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-full justify-start">
                    Subscription
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[95vw] max-w-2xl p-0" align="end" sideOffset={8}>
                  <div className="relative">
                    <Button 
                      variant="ghost" 
                      size="icon" 
                      className="absolute right-1 top-1 md:right-2 md:top-2 z-10 h-8 w-8 md:h-10 md:w-10"
                      onClick={() => setIsSubscriptionOpen(false)}
                      aria-label="Close subscription dialog"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                    <div className="p-4 md:p-6 pt-10 md:pt-12">
                      <h2 className="text-base md:text-lg font-bold mb-1">MindNote Pro</h2>
                      <p className="text-xs md:text-sm text-muted-foreground mb-3 md:mb-4">Choose your subscription plan</p>
                      
                      {/* Current Subscription Status */}
                      {profile && (
                        <div className="mb-3 md:mb-4 p-2 md:p-3 bg-muted/50 rounded-lg">
                          <p className="text-xs font-semibold mb-1">Current Status</p>
                          <div className="flex items-center justify-between text-xs">
                            <span className="text-muted-foreground">Plan:</span>
                            <span className="font-medium capitalize">
                              {profile.subscription_type || profile.subscription_status || 'Trial'}
                            </span>
                          </div>
                          {profile.subscription_end_date && (
                            <div className="flex items-center justify-between text-xs mt-1">
                              <span className="text-muted-foreground">Expires:</span>
                              <span className="font-medium">
                                {new Date(profile.subscription_end_date).toLocaleDateString()}
                              </span>
                            </div>
                          )}
                        </div>
                      )}
                      
                      <SubscriptionPlans />
                    </div>
                  </div>
                </PopoverContent>
              </Popover>
            </div>
          </PopoverContent>
        </Popover>
        
        <Button variant="ghost" size="icon" onClick={onLogout} aria-label="Log out">
          <LogOut className="h-5 w-5" />
        </Button>
      </div>
    </div>
  );
};

export default Header;
